/**
 * Draft-night clocks, for a commissioner to set. No rule governs them.
 *
 * Pick lengths are bands over ranges of rounds. Overlaps are refused — two bands covering round 5
 * makes the answer depend on array order. Gaps are fine and reported.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { LEAGUE_2026 } from '../domain/config.js';
import { SUGGESTED_CLOCKS, bandProblems, uncovered } from '../domain/clocks.js';
import type { ClockBand, DraftClocks } from '../store/types.js';

const ROUNDS = LEAGUE_2026.draftRounds;

interface Props {
  clocks: DraftClocks | undefined;
  canEdit: boolean;
  onChange: (next: DraftClocks) => void;
  /** The card's own foot — the save state and button — supplied by the page that owns the document. */
  foot?: ReactNode;
}

/** A minute:second reading of a seconds figure, for the summary line. */
const mmss = (s: number) => (s % 60 === 0 ? `${s / 60} min` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);

export function ClockSettings({ clocks, canEdit, onChange, foot }: Props) {
  const [bands, setBands] = useState<ClockBand[]>([]);
  const [nominate, setNominate] = useState('');
  const [sells, setSells] = useState('');
  const [interRound, setInterRound] = useState('');
  const [interMins, setInterMins] = useState('');

  useEffect(() => {
    setBands(clocks?.pickBands ?? []);
    setNominate(clocks?.nominateSeconds ? String(clocks.nominateSeconds) : '');
    setSells(clocks?.bidSellsAfterSeconds ? String(clocks.bidSellsAfterSeconds) : '');
    setInterRound(clocks?.intermissionAfterRound ? String(clocks.intermissionAfterRound) : '');
    setInterMins(clocks?.intermissionMinutes ? String(clocks.intermissionMinutes) : '');
  }, [clocks]);

  const problems = bandProblems(bands, ROUNDS);
  const gaps = uncovered(bands, ROUNDS);

  /** Everything the parent needs to save, assembled from the inputs as they stand. */
  const assemble = (over: Partial<DraftClocks> = {}): DraftClocks => ({
    pickBands: bands,
    nominateSeconds: nominate ? Number(nominate) : null,
    bidSellsAfterSeconds: sells ? Number(sells) : null,
    intermissionAfterRound: interRound ? Number(interRound) : null,
    intermissionMinutes: interMins ? Number(interMins) : null,
    ...over,
  });

  const push = (over: Partial<DraftClocks> = {}) => onChange(assemble(over));

  const setBand = (i: number, patch: Partial<ClockBand>) => {
    const next = bands.map((b, j) => (j === i ? { ...b, ...patch } : b));
    setBands(next);
    onChange(assemble({ pickBands: next }));
  };

  return (
    <section className="card clocks">
      <div className="cardhead">
        <h3>Draft-night clocks</h3>
        <span className="mut">Nothing auto-picks. Only the farm bid clock ends anything.</span>
      </div>

      {/*
        SPLIT. The round bands on the left, as one-line rule-rows, because there may be several and
        they read as a table; the two other clocks on the right, because there is one of each.
        Below 900px the halves stack and the rule-rows keep their one-line shape.
      */}
      <div className="clocksplit">
        <div className="bands">
          {bands.map((b, i) => (
            <div className="bandrow" key={i}>
              <span className="bandlabel">Rounds</span>
              <input type="number" min={1} max={ROUNDS} value={b.fromRound} disabled={!canEdit}
                aria-label={`Band ${i + 1} first round`}
                onChange={(e) => setBand(i, { fromRound: Number(e.target.value) })} />
              <span className="mut">–</span>
              <input type="number" min={1} max={ROUNDS} value={b.toRound} disabled={!canEdit}
                aria-label={`Band ${i + 1} last round`}
                onChange={(e) => setBand(i, { toRound: Number(e.target.value) })} />
              <input className="secs" type="number" min={5} step={5} value={b.seconds} disabled={!canEdit}
                aria-label={`Band ${i + 1} seconds`}
                onChange={(e) => setBand(i, { seconds: Number(e.target.value) })} />
              <span className="mut">sec{b.seconds > 0 ? ` · ${mmss(b.seconds)}` : ''}</span>
              {canEdit && (
                <button type="button" className="linkbtn danger"
                  onClick={() => {
                    const next = bands.filter((_, j) => j !== i);
                    setBands(next); onChange(assemble({ pickBands: next }));
                  }}>
                  Remove
                </button>
              )}
            </div>
          ))}
          {bands.length === 0 && (
            <p className="fact">No pick clock — the bar shows elapsed time.</p>
          )}
          <div className="bandadd">
            {canEdit && (
              <button type="button" className="linkbtn"
                onClick={() => {
                  const last = bands[bands.length - 1];
                  const from = last ? Math.min(ROUNDS, last.toRound + 1) : 1;
                  const next = [...bands, { fromRound: from, toRound: ROUNDS, seconds: 60 }];
                  setBands(next); onChange(assemble({ pickBands: next }));
                }}>
                + Add a band
              </button>
            )}
            {canEdit && bands.length === 0 && (
              <button type="button" className="linkbtn" title="Two minutes through round 4, then a minute"
                onClick={() => {
                  setBands(SUGGESTED_CLOCKS.pickBands ?? []);
                  onChange(assemble({ pickBands: SUGGESTED_CLOCKS.pickBands ?? [] }));
                }}>
                Use a suggested pair
              </button>
            )}
            <span className="mut">gaps are legal — rounds without a clock show elapsed time</span>
          </div>
          {problems.length > 0 && (
            <ul className="bandbad">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
          )}
        </div>

        <div className="otherclocks">
          <div className="clockline">
            <span className="clockname">Intermission</span>
            <label>after round
              <input type="number" min={1} max={ROUNDS} value={interRound} disabled={!canEdit}
                onChange={(e) => { setInterRound(e.target.value); push({ intermissionAfterRound: e.target.value ? Number(e.target.value) : null }); }} />
            </label>
            <label>for
              <input type="number" min={1} value={interMins} disabled={!canEdit}
                onChange={(e) => { setInterMins(e.target.value); push({ intermissionMinutes: e.target.value ? Number(e.target.value) : null }); }} />
              <span className="mut">min</span>
            </label>
          </div>
          <div className="clockline">
            <span className="clockname">Farm auction</span>
            <label>nominate
              <input type="number" min={10} step={5} value={nominate} disabled={!canEdit}
                onChange={(e) => { setNominate(e.target.value); push({ nominateSeconds: e.target.value ? Number(e.target.value) : null }); }} />
            </label>
            <label>· bid
              <input type="number" min={5} step={5} value={sells} disabled={!canEdit}
                onChange={(e) => { setSells(e.target.value); push({ bidSellsAfterSeconds: e.target.value ? Number(e.target.value) : null }); }} />
              <span className="mut">sec</span>
            </label>
          </div>
        </div>
      </div>

      {/* One foot line for both notes; the bid clock's sentence is amber because it is the one that ends something. */}
      <div className="cardfoot clockfoot">
        <span className="fact">
          {interRound && interMins
            ? `The intermission is announced on the board after round ${interRound}. `
            : 'No intermission scheduled; pause still works any time. '}
          {gaps.length > 0 && problems.length === 0 && `No clock on round${gaps.length === 1 && !gaps[0]!.includes('–') ? '' : 's'} ${gaps.join(', ')}. `}
          <span className="warn">
            {sells ? 'The farm bid clock is the one clock that ends something.' : 'Bid clock unset — press Sold yourself.'}
          </span>
        </span>
        {foot && <span className="clockacts">{foot}</span>}
      </div>
    </section>
  );
}
